import { CustomizedUnderline } from "@circle-react-shared/uikit/TipTap/Extensions/CustomizedUnderline";
import { DesktopKeymap } from "@circle-react-shared/uikit/TipTap/Extensions/CustomKeymaps/Desktop";
import { MobileKeymap } from "@circle-react-shared/uikit/TipTap/Extensions/CustomKeymaps/Mobile";
import { HighlightedCodeBlock } from "@circle-react-shared/uikit/TipTap/Extensions/HighlightedCodeBlock";
import { CustomizedListItem } from "@circle-react-shared/uikit/TipTapBlockEditor/CustomizedListItemExtension";
import { CustomizedOrderedList } from "@circle-react-shared/uikit/TipTapBlockEditor/CustomizedOrderedListExtension";
import { DEFAULT_VARIABLES } from "@circle-react-uikit/InsertVariableButton";
import { InlineEmojiExtension } from "@circle-react/components/shared/uikit/TipTapBlockEditor/InlineEmojiExtension";
import type { Extensions } from "@tiptap/core";
import Blockquote from "@tiptap/extension-blockquote";
import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import Highlight from "@tiptap/extension-highlight";
import Italic from "@tiptap/extension-italic";
import { BulletList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import Text from "@tiptap/extension-text";
import {
  CharacterCount,
  UndoRedo as History,
  Placeholder,
} from "@tiptap/extensions";
import { isMobile } from "react-device-detect";

import { ChatMessageLinkExtension } from "./CustomExtensions/ChatMessageLink";
import { CustomizedLink } from "./CustomExtensions/CustomizedLink";
import { EditedLabelExtension } from "./CustomExtensions/EditedLabel";
import { EmojiReplacer } from "./CustomExtensions/EmojiReplacer";
import { EntitiesExtension } from "./CustomExtensions/Entities";
import { MentionsExtension } from "./CustomExtensions/Mentions";

export interface TiptapExtensionProps {
  frameDocument?: Document | null;
  maxLength?: null | number;
  menubarProps: any;
  placeholder: string;
  portalTargetId?: string | null;
  shouldDisableEntities: boolean;
  shouldDisableMentions: boolean;
  shouldInsertLineBreakOnEnter?: boolean;
  shouldLimitEntitiesToSpaces?: boolean;
  shouldSupportCodeBlocks: boolean;
  shouldSupportHeadings?: boolean;
  shouldSupportTables: boolean;
  type: "workflow" | "chat";
}

export const getTiptapExtensions = ({
  placeholder,
  maxLength = null,
  menubarProps,
  type,
  shouldDisableEntities = false,
  shouldDisableMentions = false,
  shouldInsertLineBreakOnEnter = false,
  shouldLimitEntitiesToSpaces = false,
  portalTargetId = null,
  shouldSupportCodeBlocks = false,
  shouldSupportHeadings = false,
  shouldSupportTables = false,
  frameDocument = null,
}: TiptapExtensionProps): Extensions => {
  const isWorkflowType = type == "workflow";

  return [
    ...(shouldSupportTables
      ? [
          Table.configure({ resizable: false }),
          TableCell,
          TableHeader,
          TableRow,
        ]
      : []),
    ...(shouldSupportCodeBlocks ? [HighlightedCodeBlock] : []),
    // Same levels as the backend HTML to TipTap parser.
    ...(shouldSupportHeadings ? [Heading.configure({ levels: [2, 3] })] : []),
    Document,
    Paragraph,
    Text,
    Bold,
    Blockquote,
    Italic,
    Strike,
    Highlight.configure({
      HTMLAttributes: {
        class: "highlight-class",
      },
    }),
    CustomizedUnderline,
    EditedLabelExtension,
    History,
    isMobile || shouldInsertLineBreakOnEnter ? MobileKeymap : DesktopKeymap,
    CustomizedLink.configure({
      openOnClick: false,
      autolink: true,
      shouldAutoLink: (href: any) => {
        const dynamicVariableLabels: any = (
          menubarProps.dynamicVariables || DEFAULT_VARIABLES
        ).map(({ label }: any) => label.replace(/{{|}}/g, ""));
        return !dynamicVariableLabels.some((variable: any) =>
          variable.includes(href),
        );
      },
    }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: "is-editor-empty",
    }),
    ...(!isWorkflowType && !shouldDisableMentions
      ? [
          MentionsExtension.configure({
            portalTargetId,
            frameDocument,
            useCompassSuggestionMenu: true,
          }),
        ]
      : []),
    ...(!shouldDisableEntities
      ? [
          EntitiesExtension.configure({
            portalTargetId,
            frameDocument,
            useCompassSuggestionMenu: true,
            isSpacesOnly: shouldLimitEntitiesToSpaces,
          }),
        ]
      : []),
    ...(!shouldDisableEntities && !shouldLimitEntitiesToSpaces
      ? [ChatMessageLinkExtension]
      : []),
    HardBreak.extend({
      addKeyboardShortcuts() {
        // Disable all keyboard shortcuts for hard break
        return {};
      },
    }),
    EmojiReplacer,
    CharacterCount.configure({
      limit: maxLength,
    }),
    BulletList,
    CustomizedOrderedList,
    CustomizedListItem.configure({ type }),
    InlineEmojiExtension.configure({
      portalTargetId,
      frameDocument,
      useCompassSuggestionMenu: true,
    }),
  ];
};
